import React, { useEffect, useState } from 'react';
import { axiosKK } from '../../../services/networkRequest';
import { WarehouseLocation } from '../../../models/APIModels';
import errorMessage from '../../../services/errorMessage';
import {
  AddButton,
  AdminNotice,
  DataTable,
  DataTableColumn,
  InlineEditor,
  InlineEditorField,
} from '../../../components/admin/Shared';
import { AdminPartnerType } from '../../../components/admin/CreatePartnerModal';
import { fetchHubspotOptions, hubspotKey } from '../../../services/hubspotSync';

interface Props {
  partnerId: number;
  /** The partner's type, when known — a courier-only partner gets a word of caution. */
  partnerType?: AdminPartnerType;
}

interface WarehouseFormValues extends Record<string, unknown> {
  name: string;
  address: string;
  city: string;
  postcode: string;
  hubspotName: string;
}

const BASE_FIELDS: InlineEditorField[] = [
  { name: 'name', label: 'Name', type: 'text', required: true, placeholder: 'e.g. Cowley' },
  { name: 'address', label: 'Address', type: 'textarea', required: true },
  { name: 'city', label: 'Town or city', type: 'text' },
  { name: 'postcode', label: 'Postcode', type: 'text', placeholder: 'e.g. OX4 4AA' },
];

/**
 * The HubSpot Warehouse option a location stands for: a pick from HubSpot's list,
 * or typed when the list could not be loaded. An order HubSpot sends to that
 * option is stored here, and an order stored here is sent to HubSpot as it.
 */
export function hubspotNameField(options: string[], current?: string): InlineEditorField {
  if (options.length === 0) {
    return {
      name: 'hubspotName',
      label: 'HubSpot name',
      type: 'text',
      hint: 'HubSpot’s list could not be loaded. Type the name exactly as HubSpot shows it, or leave it blank.',
    };
  }
  const known = options.some((o) => hubspotKey(o) === hubspotKey(current));
  return {
    name: 'hubspotName',
    label: 'HubSpot name',
    type: 'select',
    options: [
      { value: '', label: '— Not linked to HubSpot —' },
      ...(current && !known ? [{ value: current, label: `${current} (not in HubSpot’s list)` }] : []),
      ...options.map((o) => ({ value: o, label: o })),
    ],
  };
}

/**
 * Where a warehouse partner keeps boxes. Orders are assigned to a location, not
 * to the partner, so until a partner has one it is missing from the Assign
 * modal's warehouse list and its warehouse dashboard has nothing to show.
 * Locations are switched off rather than deleted, because orders refer to them.
 */
export default function WarehousesTab({ partnerId, partnerType }: Props) {
  const [locations, setLocations] = useState<WarehouseLocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [netError, setNetError] = useState<string>();
  const [saveBanner, setSaveBanner] = useState<string>();

  const [editingId, setEditingId] = useState<number | 'new' | null>(null);
  const [editingSeed, setEditingSeed] = useState<Partial<WarehouseFormValues>>({});
  const [submitting, setSubmitting] = useState(false);
  const [hubspotOptions, setHubspotOptions] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    fetchHubspotOptions().then((o) => {
      if (!cancelled) setHubspotOptions(o.warehouse);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const refresh = React.useCallback(() => {
    setLoading(true);
    axiosKK
      .get<{ warehouses: WarehouseLocation[] }>(`/admin/partners/${partnerId}/warehouses`)
      .then(({ data }) => setLocations(data.warehouses || []))
      .catch((err) => errorMessage(setNetError)(err))
      .finally(() => setLoading(false));
  }, [partnerId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const openCreate = () => {
    setSaveBanner(undefined);
    setEditingId('new');
    setEditingSeed({ name: '', address: '', city: '', postcode: '', hubspotName: '' });
  };

  const openEdit = (w: WarehouseLocation) => {
    setSaveBanner(undefined);
    setEditingId(w.id);
    setEditingSeed({
      name: w.name,
      address: w.address,
      city: w.city ?? '',
      postcode: w.postcode ?? '',
      hubspotName: w.hubspotName ?? '',
    });
  };

  const submit = (values: WarehouseFormValues) => {
    setNetError(undefined);
    setSaveBanner(undefined);
    const hubspotName = String(values.hubspotName ?? '').trim();
    const payload: Record<string, string | null> & { name: string; address: string } = {
      name: String(values.name ?? '').trim(),
      address: String(values.address ?? '').trim(),
      city: String(values.city ?? '').trim(),
      postcode: String(values.postcode ?? '').trim().toUpperCase(),
    };
    // Sent when it changed: null unlinks, and a new link reassigns the live
    // orders HubSpot already sends to it.
    if (hubspotName !== String(editingSeed.hubspotName ?? '').trim()) {
      payload.hubspotName = hubspotName || null;
    }
    if (!payload.name || !payload.address) {
      setNetError('Please enter a name and an address.');
      return;
    }
    // Checked here as well as by the API, so the admin is told which location
    // clashes — master's errorMessage shows a generic sentence for any conflict.
    const clash = locations.find(
      (w) => w.id !== editingId && w.name.trim().toLowerCase() === payload.name.toLowerCase()
    );
    if (clash) {
      setNetError(`This partner already has a location called ${clash.name}.`);
      return;
    }
    const wasNew = editingId === 'new';
    setSubmitting(true);
    const request = wasNew
      ? axiosKK.post(`/admin/partners/${partnerId}/warehouses`, payload)
      : axiosKK.patch(`/admin/partners/${partnerId}/warehouses/${editingId}`, payload);
    request
      .then(() => {
        setEditingId(null);
        setSaveBanner(
          wasNew
            ? `${payload.name} added. It can now be chosen as an order's warehouse.`
            : `${payload.name} updated.`
        );
        refresh();
      })
      .catch((err) => errorMessage(setNetError)(err))
      .finally(() => setSubmitting(false));
  };

  const setActive = (w: WarehouseLocation, isActive: boolean) => {
    setNetError(undefined);
    setSaveBanner(undefined);
    axiosKK
      .patch(`/admin/partners/${partnerId}/warehouses/${w.id}`, { isActive })
      .then(() => {
        setSaveBanner(
          isActive
            ? `${w.name} switched back on.`
            : `${w.name} switched off. It is no longer offered when assigning orders.`
        );
        refresh();
      })
      .catch((err) => errorMessage(setNetError)(err));
  };

  const columns: DataTableColumn<WarehouseLocation>[] = [
    { key: 'name', label: 'Name' },
    { key: 'address', label: 'Address' },
    { key: 'city', label: 'Town or city', render: (w) => w.city || '—' },
    { key: 'postcode', label: 'Postcode', render: (w) => w.postcode || '—' },
    { key: 'hubspotName', label: 'HubSpot name', render: (w) => w.hubspotName || 'Not linked' },
    { key: 'liveOrderCount', label: 'Live orders' },
    {
      key: 'isActive',
      label: 'Status',
      render: (w) =>
        w.isActive ? (
          <span className="logistics-badge logistics-badge--active">Active</span>
        ) : (
          <span className="logistics-badge logistics-badge--inactive">Switched off</span>
        ),
    },
  ];

  return (
    <section className="admin-partner-warehouses">
      <AdminNotice intent="error">{netError}</AdminNotice>
      <AdminNotice intent="success">{saveBanner}</AdminNotice>

      <p className="admin-partner-warehouses__intro">
        Orders are assigned to a warehouse location, not to the partner, so a
        warehouse partner needs at least one before it can be given an order.
      </p>
      {partnerType === 'COURIER' && (
        <AdminNotice intent="info">
          This partner is set up as a courier. Add a location only if they also
          store boxes.
        </AdminNotice>
      )}

      {loading ? (
        <p>Loading warehouse locations…</p>
      ) : (
        <>
          {editingId !== null && (
            <InlineEditor<WarehouseFormValues>
              key={String(editingId)}
              title={editingId === 'new' ? 'New warehouse location' : 'Edit warehouse location'}
              fields={[...BASE_FIELDS, hubspotNameField(hubspotOptions, editingSeed.hubspotName as string | undefined)]}
              initialValues={editingSeed}
              isSubmitting={submitting}
              onSubmit={submit}
              onCancel={() => setEditingId(null)}
            />
          )}

          <DataTable<WarehouseLocation>
            columns={columns}
            rows={locations}
            keyOf={(w) => w.id}
            emptyMessage="No warehouse locations yet."
            aboveTable={
              <AddButton onClick={openCreate} disabled={editingId !== null}>
                Add warehouse location
              </AddButton>
            }
            actionsColumn={{
              render: (w) => {
                const held = w.isActive && w.liveOrderCount > 0;
                return (
                  <>
                    <button
                      type="button"
                      className="pricing-table__remove"
                      onClick={() => openEdit(w)}
                      disabled={editingId !== null}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="pricing-table__remove"
                      onClick={() => setActive(w, !w.isActive)}
                      disabled={held}
                      title={
                        held
                          ? `${w.liveOrderCount} live ${w.liveOrderCount === 1 ? 'order is' : 'orders are'} assigned here. Move ${w.liveOrderCount === 1 ? 'it' : 'them'} to another warehouse first.`
                          : undefined
                      }
                    >
                      {w.isActive ? 'Switch off' : 'Switch on'}
                    </button>
                  </>
                );
              },
            }}
          />
        </>
      )}
    </section>
  );
}
